/**
 * TanStack Query hooks for `campaign-templates`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	instantiate,
	list,
	remove,
	update,
	campaignTemplatesQueryKeys,
	type CampaignTemplateDraft,
	type CampaignTemplateRow,
	type CreateCampaignTemplateBody,
	type UpdateCampaignTemplateBody,
} from "./api";

export function useCampaignTemplatesList(params?: {
	vertical?: string;
	includeInactive?: boolean;
}): UseQueryResult<CampaignTemplateRow[], Error> {
	return useQuery({
		queryKey: params
			? campaignTemplatesQueryKeys.list(params)
			: campaignTemplatesQueryKeys.list(),
		queryFn: () => list(params),
	});
}

export function useCampaignTemplatesCreate(): UseMutationResult<
	CampaignTemplateRow,
	Error,
	CreateCampaignTemplateBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignTemplatesQueryKeys.all });
		},
	});
}

export function useCampaignTemplatesUpdate(): UseMutationResult<
	CampaignTemplateRow,
	Error,
	{ id: string; patch: UpdateCampaignTemplateBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, patch }) => update(id, patch),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignTemplatesQueryKeys.all });
		},
	});
}

export function useCampaignTemplatesDelete(): UseMutationResult<
	{ ok: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignTemplatesQueryKeys.all });
		},
	});
}

export function useCampaignTemplatesInstantiate(): UseMutationResult<
	CampaignTemplateDraft,
	Error,
	string
> {
	return useMutation({
		mutationFn: (code: string) => instantiate(code),
	});
}
